import type { Object3D } from 'three';
import {
	isAbstractSketchEdgeObject,
	isSketchCollectionObject,
	isSketchPointObject,
	isSketchSlotObject,
	type AbstractSketchEdgeObject,
	type CollectSource,
	type ISketchObject,
	type SketchCollectionObject,
	type SketchGroup,
	SketchCollectionCreationSource,
	SketchPointBoxSelectionRole,
	SketchPointKind,
	type SketchPointObject,
	type SketchSlotObject,
} from '../../mesh/sketch';
import {
	isSketchRelationContainer,
	type SketchRelationContainer,
} from '../solver/constraint/SketchRelationContainer';
import type { SketchRelationDefinition } from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {detachConstraintFromAllTargets} from '../solver/constraint/utils/detachConstraintFromAllTargets';
import type {
	SketchEntityDeleteInput,
	SketchEntityDeleteResult,
} from './SketchEntityDeleteTypes';
import { SKETCH_ENTITY_DELETE_COMMIT_REASON } from './SketchEntityDeleteCommitReason';

/**
 * 可删除草图实体。
 */
type DeletableSketchEntity =
	| AbstractSketchEdgeObject
	| SketchPointObject
	| SketchCollectionObject
	| SketchSlotObject;

/**
 * 删除过程中只需要访问约束容器的草图对象协议。
 * 说明：草图组内也可能存在 Slot/Collection 等非 ISketchObject 对象，不能把完整集合强行收窄为 ISketchObject。
 */
type ConstraintOwner = {
	/** 与对象关联的约束容器。 */
	constraints: SketchRelationContainer;
};

//------ 矩形构造线交点删除 开始------
/**
 * 矩形构造线几何数据片段。
 */
interface SketchRectangleConstructionLineGeometryData {
	/** 矩形构造线数据。 */
	constructionLines?: {
		/** 构造线辅助点对象 ID 列表。 */
		pointIds?: unknown[];
	};
}
//------ 矩形构造线交点删除 结束------

/**
 * 草图实体删除服务。
 *
 * 设计说明：
 * 1. 本服务只负责 CAD 内核里的草图实体摘除，不直接处理引用仓储；
 * 2. 本服务返回全部已删对象的稳定持久 ID，由上层事务编排器完成尺寸等领域仓库联动；
 * 3. 删除实体时同步清理孤立端点与失效约束，保持草图对象图谱自洽。
 */
export class SketchEntityDeleteService {
	/**
	 * 删除一组草图实体。
	 *
	 * @param input 删除输入。
	 * @returns 删除结果。
	 */
	public deleteEntities(input: SketchEntityDeleteInput): SketchEntityDeleteResult {
		const constraintOwnerSnapshot = this.collectConstraintOwners(input.sketchGroup);
		const deletedObjects = new Set<Object3D>();
		const skippedObjectIds: string[] = [];
		const requestedEntities = this.resolveRequestedEntities(input);
		//------ 删除后轮廓闭合重判 开始------
		let recreatedClosureObjectIds: string[] = [];
		let changedObjectIds: string[] = [];
		//------ 删除后轮廓闭合重判 结束------

		requestedEntities.forEach((entity) => {
			if (deletedObjects.has(entity)) {
				return;
			}
			this.deleteResolvedEntity(entity, deletedObjects, input.syncContour === true);
		});

		input.objects.forEach((object) => {
			if (!requestedEntities.includes(object as DeletableSketchEntity)) {
				const resolvedEntity = this.resolveDeletableEntity(object, input.sketchGroup);
				if (!resolvedEntity) {
					skippedObjectIds.push(object.uuid);
				}
			}
		});

		if (deletedObjects.size > 0) {
			this.removeConstraintsReferencingDeletedObjects(
				constraintOwnerSnapshot,
				deletedObjects,
				input.sketchGroup,
			);
			//------ 矩形构造线交点删除 开始------
			const changedConstructionOwnerIds = this.removeDeletedRectangleConstructionPointReferences(
				input.sketchGroup,
				deletedObjects,
			);
			//------ 矩形构造线交点删除 结束------
			//------ 删除后轮廓闭合重判 开始------
			recreatedClosureObjectIds = this.rebuildClosedSketchCollectionsAfterDeletion(
				input.sketchGroup,
				input.syncContour === true,
			);
			const deletedObjectIds = Array.from(deletedObjects).map((object) => object.uuid);
			changedObjectIds = Array.from(new Set([
				...deletedObjectIds,
				//------ 矩形构造线交点删除 开始------
				...changedConstructionOwnerIds,
				//------ 矩形构造线交点删除 结束------
				...recreatedClosureObjectIds,
			]));
			const sourceObjectId = deletedObjectIds[0];
			//------ 删除后轮廓闭合重判 结束------
			input.sketchGroup.markDefinitionDirty({
				reason: SKETCH_ENTITY_DELETE_COMMIT_REASON,
				sourceObjectId,
				//------ 删除后轮廓闭合重判 开始------
				changedObjectIds,
				//------ 删除后轮廓闭合重判 结束------
			});
			input.sketchGroup.commitDefinitionChanges({
				reason: SKETCH_ENTITY_DELETE_COMMIT_REASON,
				sourceObjectId,
				//------ 删除后轮廓闭合重判 开始------
				changedObjectIds,
				//------ 删除后轮廓闭合重判 结束------
			});
		}

		return {
			deletedObjectIds: Array.from(deletedObjects).map((object) => object.uuid),
			// 领域仓库按持久 ID 建链，关联智能尺寸级联删除等联动统一消费本字段。
			deletedPersistentIds: Array.from(deletedObjects)
				.map((object) => (object as {persistentId?: unknown}).persistentId)
				.filter((persistentId): persistentId is string => typeof persistentId === 'string'),
			//------ 删除后轮廓闭合重判 开始------
			recreatedClosureObjectIds,
			changedObjectIds,
			//------ 删除后轮廓闭合重判 结束------
			skippedObjectIds,
		};
	}

	//------ 删除后轮廓闭合重判 开始------
	/**
	 * 删除草图实体后重新识别当前草图组内的闭合轮廓。
	 *
	 * 设计说明：
	 * 1. 删除会改变草图拓扑，旧的 `closedCollection` 关系可能失效，也可能让剩余边重新形成闭合链；
	 * 2. 这里只调用 `SketchGroup.tryFinalizeClosedSketchCollection(...)`，复用已有闭合判断和集合创建封装；
	 * 3. 删除不是渲染热路径，可以按当前草图组候选源顺序扫描，确保 Del 与右键删除都能得到一致轮廓状态。
	 *
	 * @param sketchGroup 当前活动草图组。
	 * @param syncContour 是否同步显示新识别出的上色草图轮廓。
	 * @returns 本次重新创建的闭合集合对象 ID 列表。
	 */
	private rebuildClosedSketchCollectionsAfterDeletion(
		sketchGroup: SketchGroup,
		syncContour: boolean,
	): string[] {
		const recreatedClosureObjectIds: string[] = [];
		const consumedSourceIds = new Set<string>();
		const candidateSources = sketchGroup.collectAvailableSketchClosureSources();

		candidateSources.forEach((source) => {
			if (consumedSourceIds.has(source.uuid)) {
				return;
			}

			// 以单条剩余边为种子重跑闭合解析，让统一闭合模块决定是否能收口成新轮廓。
			const collection = sketchGroup.tryFinalizeClosedSketchCollection(
				[source],
				{syncContour},
			);
			if (!collection) {
				return;
			}

			recreatedClosureObjectIds.push(collection.uuid);
			collection.getLines().forEach((collectionSource) => {
				consumedSourceIds.add(collectionSource.uuid);
			});
		});

		return recreatedClosureObjectIds;
	}
	//------ 删除后轮廓闭合重判 结束------

	/**
	 * 解析本次请求中的可删除实体并去重。
	 *
	 * @param input 删除输入。
	 * @returns 可删除实体集合。
	 */
	private resolveRequestedEntities(input: SketchEntityDeleteInput): DeletableSketchEntity[] {
		const entities: DeletableSketchEntity[] = [];
		const seenIds = new Set<string>();

		input.objects.forEach((object) => {
			const entity = this.resolveDeletableEntity(object, input.sketchGroup);
			if (!entity || seenIds.has(entity.uuid)) {
				return;
			}
			seenIds.add(entity.uuid);
			entities.push(entity);
		});

		return entities;
	}

	/**
	 * 从任意选中对象向上解析可删除草图实体。
	 *
	 * @param object 当前对象。
	 * @param sketchGroup 当前草图组。
	 * @returns 可删除实体；无法解析时返回 null。
	 */
	private resolveDeletableEntity(
		object: Object3D,
		sketchGroup: SketchGroup,
	): DeletableSketchEntity | null {
		let current: Object3D | null = object;
		while (current && current !== sketchGroup) {
			if (isAbstractSketchEdgeObject(current)) {
				return current;
			}
			if (isSketchSlotObject(current) || isSketchCollectionObject(current)) {
				return current;
			}
			if (isSketchPointObject(current) && this.isSelectableDetachedPointDeletable(current)) {
				return current;
			}
			current = current.parent;
		}

		return null;
	}

	//------ 专用交点控制点删除 开始------
	/**
	 * 判断被选择的独立点是否可直接删除。
	 *
	 * 规则说明：
	 * 1. 普通草图点仍沿用既有规则，只有无连接边时允许直接删除；
	 * 2. 矩形构造线交点与倒角原交点焦点具有独立选择语义，应支持相同的删除入口；
	 * 3. 普通曲线控制点不能因此被放开，避免误删样条、圆弧等对象的定义点。
	 * 4. 倒角焦点删除后，统一约束清理会移除存活实体中引用该点的交点与距离约束，
	 *    但不会删除两条源线、倒角线或倒角端点。
	 *
	 * @param point 草图点对象。
	 * @returns 可作为独立选中对象删除时返回 true。
	 */
	private isSelectableDetachedPointDeletable(point: SketchPointObject): boolean {
		if (point.getConnectedEdgesSnapshot().length > 0) {
			return false;
		}
		return point.pointKind === SketchPointKind.SketchPoint
			|| point.boxSelectionRole === SketchPointBoxSelectionRole.RectangleConstructionIntersection
			|| point.boxSelectionRole === SketchPointBoxSelectionRole.ChamferIntersectionFocus;
	}
	//------ 专用交点控制点删除 结束------

	/**
	 * 删除已解析出的草图实体。
	 *
	 * @param entity 草图实体。
	 * @param deletedObjects 已删除对象集合。
	 * @param syncContour 是否同步恢复上色草图轮廓。
	 */
	private deleteResolvedEntity(
		entity: DeletableSketchEntity,
		deletedObjects: Set<Object3D>,
		syncContour: boolean,
	): void {
		if (isAbstractSketchEdgeObject(entity)) {
			this.deleteEdgeEntity(entity, deletedObjects, syncContour);
			return;
		}
		if (isSketchPointObject(entity)) {
			this.deleteDetachedPoint(entity, deletedObjects);
			return;
		}
		this.deleteContainerEntity(entity, deletedObjects);
	}

	/**
	 * 删除线状草图实体并清理孤立关联点。
	 *
	 * @param edge 线状草图实体。
	 * @param deletedObjects 已删除对象集合。
	 * @param syncContour 是否同步恢复上色草图轮廓。
	 */
	private deleteEdgeEntity(
		edge: AbstractSketchEdgeObject,
		deletedObjects: Set<Object3D>,
		syncContour: boolean,
	): void {
		const parent = edge.parent;
		if (!parent) {
			return;
		}

		if (isSketchSlotObject(parent)) {
			this.deleteContainerEntity(parent, deletedObjects);
			return;
		}

		const relatedPoints = edge.getRelatedSketchPointsSnapshot();
		if (isSketchCollectionObject(parent)) {
			if (parent.creationSource === SketchCollectionCreationSource.Polygon) {
				this.deleteContainerEntity(parent, deletedObjects);
				return;
			}
			const nextSources = parent.getLines()
				.filter((source) => source.uuid !== edge.uuid) as CollectSource[];
			parent.setSources(nextSources);
			//------ 删除后轮廓闭合重判 开始------
			// setSources 会清理旧轮廓；若剩余集合仍闭合，需要立即恢复上色轮廓显示。
			if (syncContour && parent.isClosedGeometry) {
				parent.syncContour();
			}
			//------ 删除后轮廓闭合重判 结束------
			if (nextSources.length === 0) {
				this.removeObjectFromParent(parent, deletedObjects);
			}
		} else {
			this.removeObjectFromParent(edge, deletedObjects);
		}

		edge.constraints.clearRelations();
		relatedPoints.forEach((point) => {
			point.unregisterConnectedEdge(edge);
			point.unregisterRelatedSketchObject(edge);
		});
		deletedObjects.add(edge);
		relatedPoints.forEach((point) => {
			this.deleteDetachedPoint(point, deletedObjects);
		});
	}

	/**
	 * 删除槽口或集合容器实体。
	 *
	 * @param container 容器实体。
	 * @param deletedObjects 已删除对象集合。
	 */
	private deleteContainerEntity(
		container: SketchCollectionObject | SketchSlotObject,
		deletedObjects: Set<Object3D>,
	): void {
		const removedTree = this.collectObjectTree(container);
		removedTree.forEach((object) => {
			if (isAbstractSketchEdgeObject(object)) {
				object.constraints.clearRelations();
				object.getRelatedSketchPointsSnapshot().forEach((point) => {
					point.unregisterConnectedEdge(object);
					point.unregisterRelatedSketchObject(object);
				});
			}
			if (isSketchPointObject(object)) {
				object.constraints.clearRelations();
			}
		});
		this.removeObjectFromParent(container, deletedObjects);
		removedTree.forEach((object) => deletedObjects.add(object));
	}

	/**
	 * 删除已经没有关联边的草图点。
	 *
	 * @param point 草图点。
	 * @param deletedObjects 已删除对象集合。
	 */
	private deleteDetachedPoint(point: SketchPointObject, deletedObjects: Set<Object3D>): void {
		if (point.getConnectedEdgesSnapshot().length > 0) {
			return;
		}
		point.constraints.clearRelations();
		this.removeObjectFromParent(point, deletedObjects);
		deletedObjects.add(point);
	}

	//------ 矩形构造线交点删除 开始------
	/**
	 * 清理矩形集合中已删除的构造线辅助点引用。
	 *
	 * 设计说明：
	 * 1. 矩形构造线中心草图点挂在草图组下，归属关系记录在矩形集合 `geometryData.constructionLines.pointIds`；
	 * 2. 用户单独删除该点后，集合元数据必须同步移除对应 ID，避免后续拖拽、移动实体或恢复流程读取悬空引用；
	 * 3. 该清理只处理已删除点 ID，不影响构造线本体和其它仍存在的辅助点。
	 *
	 * @param sketchGroup 当前草图组。
	 * @param deletedObjects 已删除对象集合。
	 * @returns 被同步修改元数据的矩形集合 ID 列表。
	 */
	private removeDeletedRectangleConstructionPointReferences(
		sketchGroup: SketchGroup,
		deletedObjects: ReadonlySet<Object3D>,
	): string[] {
		const deletedPointIds = new Set<string>();
		deletedObjects.forEach((object) => {
			if (isSketchPointObject(object)) {
				deletedPointIds.add(object.uuid);
			}
		});
		if (deletedPointIds.size === 0) {
			return [];
		}

		const changedCollectionIds: string[] = [];
		sketchGroup.getSketchCollectionObjects().forEach((collection) => {
			const geometryData = collection.geometryData as SketchRectangleConstructionLineGeometryData | null;
			const constructionLines = geometryData?.constructionLines;
			const pointIds = constructionLines?.pointIds;
			if (!Array.isArray(pointIds)) {
				return;
			}

			const nextPointIds = pointIds.filter((pointId) => {
				return typeof pointId !== 'string' || !deletedPointIds.has(pointId);
			});
			if (nextPointIds.length === pointIds.length) {
				return;
			}

			constructionLines.pointIds = nextPointIds;
			changedCollectionIds.push(collection.uuid);
		});

		return changedCollectionIds;
	}
	//------ 矩形构造线交点删除 结束------

	/**
	 * 从父节点移除对象。
	 *
	 * @param object 目标对象。
	 * @param deletedObjects 已删除对象集合。
	 */
	private removeObjectFromParent(object: Object3D, deletedObjects: Set<Object3D>): void {
		object.parent?.remove(object);
		deletedObjects.add(object);
	}

	/**
	 * 收集对象树快照。
	 *
	 * @param root 根对象。
	 * @returns 对象树集合。
	 */
	private collectObjectTree(root: Object3D): Object3D[] {
		const objects: Object3D[] = [];
		root.traverse((object) => {
			objects.push(object);
		});
		return objects;
	}

	/**
	 * 收集当前草图内可承载约束的对象。
	 *
	 * @param sketchGroup 草图组。
	 * @returns 约束宿主对象快照。
	 */
	private collectConstraintOwners(sketchGroup: SketchGroup): ConstraintOwner[] {
		const owners: ConstraintOwner[] = [];
		const constraintTargets = [
			...sketchGroup.getSketchObjects(),
			...sketchGroup.getSketchReferenceTargets(),
		];
		constraintTargets.forEach((object) => {
			const constraints = (object as Partial<ISketchObject>).constraints;
			if (isSketchRelationContainer(constraints)) {
				owners.push({ constraints });
			}
		});
		return owners;
	}

	/**
	 * 清理仍存活对象中指向已删除实体的求解约束。
	 *
	 * @param constraintOwners 约束宿主快照。
	 * @param deletedObjects 已删除对象集合。
	 * @param sketchGroup 当前草图组，用于回收失去最后关系的外部引用目标。
	 */
	private removeConstraintsReferencingDeletedObjects(
		constraintOwners: ConstraintOwner[],
		deletedObjects: ReadonlySet<Object3D>,
		sketchGroup: SketchGroup,
	): void {
		const hasDeletedTarget = (constraint: SketchRelationDefinition): boolean => (
			constraint.getTargetsSnapshot().some((target) => deletedObjects.has(target as unknown as Object3D))
		);

		const removedRelations = new Set<SketchRelationDefinition>();
		constraintOwners.forEach((owner) => {
			if (!isSketchRelationContainer(owner.constraints)) {
				return;
			}
			owner.constraints.getRelations().forEach((constraint) => {
				if (hasDeletedTarget(constraint) && !removedRelations.has(constraint)) {
					removedRelations.add(constraint);
					detachConstraintFromAllTargets(constraint);
				}
			});
		});

		// 永久删除在历史 after 捕获前完成；孤儿 provider 必须同步退出主状态并释放 resolver。
		sketchGroup.getSketchReferenceTargets().forEach((referenceTarget) => {
			if (referenceTarget.constraints.getRelations().length > 0) {
				return;
			}
			sketchGroup.removeSketchReferenceTarget(referenceTarget);
			referenceTarget.dispose();
		});
	}
}
